import React from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { ErrorOutlineOutlined, SearchOutlined } from "@mui/icons-material"
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid-pro"
import DataGridLocaleText from "common/DataGridLocaleText"
import HighlightedText from "./HighlightedText"
import { money } from "./utils"

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
  </GridToolbarContainer>
)

const DEFAULT_COLUMN_VISIBILITY = {
  code: true,
  shortDescription: true,
  unit: true,
  unitPrice: true,
  source: true,
  breakdownStatus: true,
}

const PartBrowserTable = ({
  rows,
  selectedId,
  query,
  loading,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  onSelectRow,
  onResetFilters,
}) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState(
    DEFAULT_COLUMN_VISIBILITY,
  )

  const columns = [
    {
      field: "code",
      headerName: i18n.t("partBrowser.table.columnCode"),
      width: 110,
      renderCell: params => (
        <Typography sx={{ fontSize: 13, fontFamily: "monospace" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "shortDescription",
      headerName: i18n.t("partBrowser.table.columnDescription"),
      flex: 1,
      minWidth: 200,
      renderCell: params => (
        <Box sx={{ py: 0.5 }}>
          <HighlightedText
            text={params.row.shortDescription}
            query={query}
            sx={{ fontSize: 14, fontWeight: 700 }}
          />
        </Box>
      ),
    },
    {
      field: "unit",
      headerName: i18n.t("partBrowser.table.columnUnit"),
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <Typography sx={{ fontSize: 14 }}>{params.value}</Typography>
      ),
    },
    {
      field: "unitPrice",
      headerName: i18n.t("partBrowser.table.columnUnitPrice"),
      width: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: params => (
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {money(params.value)}
        </Typography>
      ),
    },
    {
      field: "source",
      headerName: i18n.t("partBrowser.table.columnSource"),
      width: 160,
      renderCell: params => (
        <Typography sx={{ fontSize: 13 }}>{params.value}</Typography>
      ),
    },
    {
      field: "breakdownStatus",
      headerName: i18n.t("partBrowser.table.columnBreakdown"),
      width: 90,
      align: "center",
      headerAlign: "center",
      valueGetter: (value, row) =>
        row.hasBreakdown
          ? i18n.t("partBrowser.table.yes")
          : i18n.t("partBrowser.table.no"),
      renderCell: params => (
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {params.value}
        </Typography>
      ),
    },
  ]

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={row => row.id}
        localeText={DataGridLocaleText()}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => {
            if (loading)
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 2,
                  }}
                >
                  <CircularProgress size={28} sx={{ color: "#222" }} />
                  <Typography sx={{ fontSize: 14, color: "rgba(0,0,0,0.4)" }}>
                    {i18n.t("partBrowser.table.searching")}
                  </Typography>
                </Box>
              )
            // El error se comprueba antes del caso "sin resultados": un fallo de auth o un
            // 429 devuelve la lista vacía y sin esto se leería como "no existe la partida".
            if (error)
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 1.5,
                    px: 3,
                  }}
                >
                  <ErrorOutlineOutlined
                    sx={{ fontSize: 40, color: "rgba(0,0,0,0.25)" }}
                  />
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "rgba(0,0,0,0.6)",
                      textAlign: "center",
                      maxWidth: 360,
                    }}
                  >
                    {error}
                  </Typography>
                </Box>
              )
            if (query.trim().length < 3)
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 1.5,
                  }}
                >
                  <SearchOutlined
                    sx={{ fontSize: 40, color: "rgba(0,0,0,0.18)" }}
                  />
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "rgba(0,0,0,0.5)",
                    }}
                  >
                    {i18n.t("partBrowser.table.emptyTitle")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "rgba(0,0,0,0.35)",
                      textAlign: "center",
                      maxWidth: 280,
                    }}
                  >
                    {i18n.t("partBrowser.table.emptySubtitle")}
                  </Typography>
                </Box>
              )
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "rgba(0,0,0,0.4)",
                  fontSize: 14,
                }}
              >
                {i18n.t("partBrowser.table.noResults")}
              </Box>
            )
          },
        }}
        hideFooter
        hideFooterSelectedRowCount
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnSorting
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        onRowClick={params => onSelectRow(params.row.id)}
        getRowClassName={params =>
          params.row.id === selectedId ? "Mui-selected" : ""
        }
        getRowHeight={() => "auto"}
        sx={{
          flex: 1,
          border: 0,
          "& .MuiDataGrid-toolbarContainer > Button": {
            color: "rgba(0,0,0,0.87)",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "rgba(0,0,0,0.04)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
          },
          "& .MuiDataGrid-cell": {
            alignItems: "flex-start",
            py: 0.5,
          },
        }}
      />

      {/* Con filas ya cargadas el noRowsOverlay no se monta, así que un fallo al paginar
          (típicamente un 429) sólo es visible aquí. */}
      {error && !!rows.length && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 1.25,
            px: 2,
            borderTop: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <ErrorOutlineOutlined
            sx={{ fontSize: 18, color: "rgba(0,0,0,0.4)" }}
          />
          <Typography sx={{ fontSize: 13, color: "rgba(0,0,0,0.6)" }}>
            {error}
          </Typography>
        </Box>
      )}

      {hasMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1.5,
            borderTop: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            disabled={loadingMore}
            onClick={onLoadMore}
            startIcon={loadingMore ? <CircularProgress size={14} /> : null}
            sx={{ fontSize: 13, color: "#222", borderColor: "rgba(0,0,0,0.2)" }}
          >
            {loadingMore
              ? i18n.t("partBrowser.table.loading")
              : i18n.t("partBrowser.table.loadMore")}
          </Button>
        </Box>
      )}
    </Box>
  )
}

PartBrowserTable.propTypes = {
  rows: PropTypes.array.isRequired,
  selectedId: PropTypes.string,
  query: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onSelectRow: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
}

PartBrowserTable.defaultProps = {
  selectedId: null,
  loading: false,
  error: null,
  hasMore: false,
  loadingMore: false,
  onLoadMore: null,
}

export default PartBrowserTable
